import { inputClassNames } from 'components/forms/input';
import { IBasketVoucher } from 'lib/basket/types';
import { useBasket } from 'lib/hooks/basket/use-basket';
import { useInput, ValidationResult } from 'lib/hooks/use-input';
import { IVerifyVoucher } from 'pages/api/validate-voucher';
import { useState } from 'react';
import currency from 'currency.js';
import { BookCardDataFragment } from 'lib/graphql-generated';

export const VouchersSection = () => {
  const [basket, modifyBasket] = useBasket();
  const voucher = useInput<string>(
    '',
    async (rawValue): Promise<ValidationResult> => {
      const value = rawValue.trim();
      if (!value) {
        return { valid: false, message: 'Please enter a voucher code' };
      }
      if (value.length !== 8) {
        return {
          valid: false,
          message: 'Voucher codes must be 8 characters long',
        };
      }
      try {
        const res = (await (
          await fetch(`/api/validate-voucher`, {
            method: 'POST',
            body: JSON.stringify({
              voucher: value,
              basket: basket,
            } as IVerifyVoucher),
          })
        ).json()) as ValidationResult;

        if (res.valid) {
          modifyBasket({
            type: 'add-voucher',
            payload: {
              id: res.data.id,
              availableAmount: res.data.availableAmount,
            },
          });
          return res;
        } else {
          return {
            valid: false,
            message: res.message || 'Voucher is not valid',
            data: res.data,
          };
        }
      } catch (err) {
        return { valid: false, message: 'Voucher is not valid' };
      }
    }
  );

  const reValidate = () => {
    voucher.validate();
  };

  if (basket.items.length > 0) {
    return (
      <>
        <li className='pb-4 pt-4 flex flex-row flex-wrap '>
          <h2
            id='voucherLabel'
            className='inline-flex font-bold text-sm flex-shrink mr-4'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-5 w-5 my-auto mr-2'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>

            <span className='my-auto'>Voucher</span>
          </h2>
          <div
            className={`${inputClassNames} ${
              voucher.isModified && !voucher.isValid
                ? 'ring-1 ring-red-default focus-within:border-red-default'
                : 'ring-green-default focus-within:border-green-default'
            } focus-within:ring-1  flex flex-row flex-1`}>
            <input
              className='border-none focus:ring-0 flex-grow'
              type='text'
              name='voucher'
              id='voucher'
              placeholder='enter voucher code'
              aria-labelledby='voucherLabel'
              {...voucher.bind}
            />
            <button
              className='ml-auto flex-shrink hover:bg-yellow-default px-2 rounded'
              type='button'
              onClick={voucher.validate}>
              Validate
            </button>
          </div>
          {voucher.isModified && voucher.validation?.message && (
            <>
              {voucher.validation.data?.length > 0 ? (
                <div
                  className={`w-full mt-4 text-sm px-2 py-4 text-center ${
                    !voucher.validation.valid
                      ? 'border border-yellow-default bg-orange-50 rounded'
                      : ''
                  }`}>
                  <p>{voucher.validation.message}</p>
                  <div className='flex flex-col space-y-2 pt-2'>
                    {voucher.validation.data.map((b: BookCardDataFragment) => (
                      <button
                        key={`${b.isbn}_voucherval`}
                        className='bg-yellow-default px-2 py-1 rounded mx-auto border border-yellow-default hover:border-black'
                        onClick={() => {
                          modifyBasket({
                            type: 'add',
                            payload: {
                              type: 'book',
                              id: b.isbn,
                              data: {
                                price: b.price,
                                discount: b.is_discounted
                                  ? b.discount_value
                                  : 0,
                                quantity: 1,
                                book: {
                                  isbn: b.isbn,
                                  image: b.image,
                                  title: b.title,
                                },
                              },
                            },
                          });
                        }}>
                        Add <u className='underline'>{b.title}</u>
                      </button>
                    ))}
                    <span>Then press validate again</span>
                  </div>
                </div>
              ) : (
                <p
                  className={`w-full mt-4 text-sm px-2 py-4 text-center ${
                    !voucher.validation.valid ? 'bg-red-100 rounded   ' : ''
                  }`}>
                  {voucher.validation.message}
                </p>
              )}
            </>
          )}
          <ul className='w-full space-y-2 pt-2'>
            {basket.discount_vouchers.map((v) => (
              <li
                key={v.id}
                className='border border-gray-200 rounded-md overflow-hidden w-full relative'>
                <button
                  className=' group absolute top-1 mt-px right-2 bg-green-default rounded-full hover:bg-white'
                  onClick={() =>
                    modifyBasket({ type: 'remove-voucher', payload: v })
                  }>
                  <span className='sr-only'>remove voucher</span>
                  <svg
                    className='w-8 h-4 text-white group-hover:text-green-default'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                    fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                </button>
                <h3 className='bg-green-default text-white text-sm px-3 py-1'>
                  {v.id}
                </h3>
                <dl className='grid grid-cols-2 divide-y text-sm'>
                  <dt className='row-start-1 px-2 py-1 text-gray-700'>
                    Voucher Balance:
                  </dt>
                  <dd className='row-start-1 px-2 py-1 text-right text-gray-700'>
                    {currency(v.availableAmount, {
                      fromCents: true,
                      symbol: '£',
                    }).format()}
                  </dd>
                </dl>
              </li>
            ))}
          </ul>
          {basket.discount_vouchers.length > 0 && (
            <p className='italic text-sm'></p>
          )}
        </li>
      </>
    );
  } else {
    return null;
  }
};
